import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';

interface Hotspot {
  x: number;
  y: number;
  title: string;
  body: string;
  below?: boolean;
}

interface ProgramItem {
  title: string;
  desc: string;
}

const hotspots: Hotspot[] = [
  {
    x: 44, y: 6, below: true,
    title: 'The Light',
    body: 'A sculptural artificial light holds the center of the room — the same warm, circadian glow as the rooms’ skylights, scaled up to hold a crowd.',
  },
  {
    x: 30, y: 34,
    title: 'The Garden',
    body: 'Part garden, part gathering place — a living wall with water streaming gently down its face into a knee-high pool of the same reclaimed brick. Walk in off the street and the city lets go of you.',
  },
  {
    x: 81, y: 36,
    title: 'The Washing Font',
    body: 'Hands, feet, and your water bottle. Basins for washing hands and feet — for wudu, for a long day, for whatever you need — with seats, built in rather than bolted on. Alongside them, still water and ice, free to refill.',
  },
  {
    x: 52, y: 40,
    title: 'The Hallway',
    body: 'Through this door, the rooms. Book whenever — app, website, even from home before you land in New York — and scan the code at your room door. You never have to come through the Hull to use a room, and you never have to use a room to enjoy the Hull.',
  },
  {
    x: 82, y: 64,
    title: 'Found & Refinished',
    body: 'Every piece is a find — refurbished church pews, Danish chairs, an ottoman someone loved before — restored and arranged like a quiet showroom. Nothing matches; everything belongs.',
  },
  {
    x: 33, y: 88,
    title: 'The Floor',
    body: 'Reclaimed NYC brick with moss pressed into the joints — the city’s own material, softened.',
  },
];

const TAB_NAMES = ['The Garden Room', 'What Happens Here', 'Just Walk In', 'The Name'];

const HullPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [spot, setSpot] = useState<number | null>(null);

  const programItems: ProgramItem[] = [
    { title: "Cultural celebrations", desc: "Islamic Heritage Month, Lunar New Year, Juneteenth, Pride, Hanukkah, Christmas, Diwali, and more—honoring the traditions that matter to New Yorkers" },
    { title: "Local artist showcases", desc: "Rotating art installations from neighborhood creators" },
    { title: "Community conversations", desc: "Panel discussions, town halls, storytelling nights" },
    { title: "Skill shares", desc: "Free workshops on everything from resume writing to urban gardening" },
    { title: "Quiet hours", desc: "Designated times when The Hull is a silent space for reading, prayer, or rest" }
  ];

  return (
    <div className="min-h-screen" style={{ background: 'var(--cream)' }}>
      <Helmet>
        <title>The Hull | A free garden room in SoHo | Maslow NYC</title>
        <link rel="canonical" href="https://maslow.nyc/hull" />
        <meta name="description" content="The Hull is the free garden room at the heart of Maslow in SoHo, New York. Water, ice, a living wall and a seat. No purchase, no questions. Walk in off the street." />
      </Helmet>

      {/* Hero Section — compact; the folder below is the hero */}
      <section className="relative pt-10 pb-4 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-maslow-navy mb-3">
              The Hull
            </h1>
            <p className="text-base md:text-lg text-[color-mix(in_srgb,var(--navy)_75%,transparent)] font-light max-w-2xl mx-auto">
              Part garden, part gathering place. All <span className="underline decoration-maslow-gold-ink decoration-2 underline-offset-4 text-maslow-navy">free</span>. Walk in, stay as long as you like.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tabbed folder */}
      <section className="px-4 md:px-6 pb-24">
        <div className="max-w-6xl mx-auto">
          {/* Tab rail */}
          <div className="flex flex-wrap gap-x-8 gap-y-1 px-2 mb-5" role="tablist" aria-label="The Hull sections">
            {TAB_NAMES.map((name, i) => (
              <button
                key={name}
                role="tab"
                aria-selected={tab === i}
                onClick={() => { setTab(i); setSpot(null); }}
                className={`relative py-2 text-[11px] uppercase tracking-[0.26em] transition-opacity duration-300 ${tab === i ? 'text-maslow-navy opacity-100' : 'text-maslow-navy opacity-70 hover:opacity-100'}`}
              >
                {name}
                <span className={`absolute left-0 right-0 bottom-0 h-px bg-maslow-gold origin-left transition-transform duration-300 ${tab === i ? 'scale-x-100' : 'scale-x-0'}`} />
              </button>
            ))}
          </div>

          {/* Navy panel */}
          <div className="bg-maslow-navy text-maslow-cream rounded-sm border border-[rgba(212,175,106,0.25)] p-6 md:p-10 shadow-[0_24px_60px_-34px_rgba(28,43,58,0.65)]">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              {/* TAB 0 — THE GARDEN ROOM */}
              {tab === 0 && (
                <div>
                  <h3 className="text-3xl md:text-4xl font-serif text-maslow-cream mb-2">A room to land in.</h3>
                  <p className="text-white/80 max-w-2xl mb-6">
                    Walk in off the street and the city lets go of you. Explore the room — each point is part of how it holds you.
                  </p>

                  {/* Hotspot stage */}
                  <div className="relative max-w-3xl mx-auto" onMouseLeave={() => setSpot(null)}>
                    <img
                      src="/hull-garden-room.webp"
                      alt="Concept render of the Hull — brick garden room with a glowing light sculpture, washing wall, daybeds, and open bays to the SoHo street"
                      width={1344}
                      height={896}
                      decoding="async"
                      className="w-full h-auto block rounded-sm"
                    />
                    <span className="absolute right-2 bottom-2 text-[10px] uppercase tracking-[0.1em] text-white/60 pointer-events-none">
                      Concept render
                    </span>
                    {hotspots.map((h, i) => (
                      <button
                        key={h.title}
                        aria-label={h.title}
                        onMouseEnter={() => setSpot(i)}
                        onClick={(e) => { e.stopPropagation(); setSpot(spot === i ? null : i); }}
                        className="absolute w-9 h-9 -translate-x-1/2 -translate-y-1/2 z-10"
                        style={{ left: `${h.x}%`, top: `${h.y}%` }}
                      >
                        <span className="absolute inset-0 m-auto w-3.5 h-3.5 rounded-full bg-maslow-gold/70 animate-ping" />
                        <span className="absolute inset-0 m-auto w-3.5 h-3.5 rounded-full bg-maslow-gold shadow-[0_0_0_2px_rgba(255,255,255,0.85)]" />
                      </button>
                    ))}
                    {hotspots.map((h, i) => (
                      <div
                        key={`card-${h.title}`}
                        className={`absolute z-20 w-72 md:w-80 max-w-[86vw] bg-maslow-cream border border-[rgba(212,175,106,0.55)] rounded-sm p-5 shadow-xl transition-opacity duration-300 ${spot === i ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                        style={{
                          left: `${Math.min(Math.max(h.x, 20), 72)}%`,
                          top: `${h.y}%`,
                          transform: h.below ? 'translate(-50%, 26px)' : 'translate(-50%, calc(-100% - 26px))',
                        }}
                      >
                        <h4 className="font-serif text-xl font-semibold text-maslow-navy mb-1.5">{h.title}</h4>
                        <p className="text-[13.5px] leading-relaxed text-[rgba(28,43,58,0.88)]">{h.body}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-center text-xs uppercase tracking-[0.18em] text-white/50 mt-3">Hover or tap a point</p>
                </div>
              )}

              {/* TAB 1 — WHAT HAPPENS HERE */}
              {tab === 1 && (
                <div>
                  <h3 className="text-3xl md:text-4xl font-serif text-maslow-cream mb-4">Cultural programming &amp; events</h3>
                  <p className="text-white/80 max-w-2xl mb-8">
                    The Hull is the neighborhood’s room as much as mine. I have ideas for how to fill it, and the best ones will come from the people who actually use it. What I’m looking at:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                    {programItems.map((item) => (
                      <div key={item.title} className="bg-white/5 border border-[rgba(212,175,106,0.18)] rounded-sm p-5">
                        <h4 className="text-maslow-gold font-medium mb-1.5 text-[15px]">{item.title}</h4>
                        <p className="text-white/80 text-[13.5px] leading-relaxed">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-white/80 max-w-2xl">
                    If there’s something you’d want here — an artist to show, a tradition to mark, a night the city needs — tell me. The room only works if it belongs to the people in it.{' '}
                    <a href="mailto:hello@maslow.nyc" className="text-maslow-gold hover:opacity-80 transition-opacity font-medium">hello@maslow.nyc</a>
                  </p>
                </div>
              )}

              {/* TAB 2 — JUST WALK IN */}
              {tab === 2 && (
                <div>
                  <h3 className="text-3xl md:text-4xl font-serif text-maslow-cream mb-6">Just walk in</h3>
                  <div className="max-w-2xl space-y-5 text-white/85 leading-relaxed">
                    <p>
                      No turnstile. No sign-in. While the Hull is open, the door is open — come in and sit down. It asks nothing of you.
                    </p>
                    <p>
                      The rooms are their own thing. Book one whenever — from the app, the website, even from home before you land in New York — and when it’s ready, scan the code at the room door to let yourself in. You never have to come through the Hull to use a room, and you never have to use a room to enjoy the Hull.
                    </p>
                    <p>
                      Stay as long as you like. The only thing I ask is that you treat the room, and the people in it, with care. Free wifi, more chargers than you’ll need, ice water — make yourself at home. A dedicated foot-washing station for wudu sits off the main room.
                    </p>
                  </div>
                  <div className="mt-8 p-5 rounded-sm bg-white/5 border border-[rgba(212,175,106,0.35)] max-w-2xl">
                    <p className="text-white/90">
                      <strong className="text-maslow-gold">Hours:</strong> Posted before we open. The rooms will keep longer hours than the Hull, through their own street entrance, so they are there after the Hull closes for the night.
                    </p>
                  </div>
                </div>
              )}

              {/* TAB 3 — THE NAME */}
              {tab === 3 && (
                <div>
                  <h3 className="text-3xl md:text-4xl font-serif text-maslow-cream mb-6">The name</h3>
                  <div className="max-w-2xl space-y-5 text-white/85 leading-relaxed">
                    <p>
                      Ships are held together by their hull—the watertight body that makes everything else possible. The crew, the cargo, the journey—none of it works without a strong hull.
                    </p>
                    <p>
                      I named this space The Hull because it’s meant to hold communities together. It’s the infrastructure that makes connection possible in a city where connection is hard to find.
                    </p>
                  </div>
                  <p className="font-serif text-2xl text-maslow-cream border-l-[3px] border-maslow-gold pl-6 mt-8 max-w-2xl">
                    Restrooms are essential. Privacy is essential. But so is community. The Hull is where those things meet.
                  </p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HullPage;
